import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import type { Session } from '#core/lib/expr/session/Session.ts';

import { createChatSession } from './createChatSession.ts';
import { CHAT_RESERVED_NAMES } from './opTables.ts';

/**
 * Hook returning a TanStack-Query-backed `Session` scoped to a
 * conversation thread id. One session per thread id: switching threads
 * gives that thread its own binding namespace + cache prefix.
 *
 * The session is keyed to `threadId` via the React-idiomatic "adjust
 * state when a prop changes during render" pattern — `useState` holds
 * `{ threadId, session }`, and a render-time mismatch creates a fresh
 * session for the new thread (NOT `useMemo`/`useEffect` — those are for
 * derived values / side effects, not identity-stable resources; React
 * 19 + React Compiler). Within a single thread id the session is stable
 * across re-renders so accumulated bindings persist.
 *
 * Bindings are NOT separately persisted. They are a projection of the
 * active message branch: `<SessionHydrationGate>` reconciles the
 * session's binding namespace from the producer tool-calls on the
 * branch (the persisted transcript is the single source of truth), so
 * reload, thread-switch, regenerate, edit and branch-switch all stay
 * consistent with what the user sees — there is no recipe log to drift.
 *
 * Mount with React 19's context-as-provider syntax:
 *
 * ```tsx
 * function Chat({ threadId, children }) {
 *   const session = useChatSession(threadId);
 *   return (
 *     <SessionContext value={session}>
 *       {children}
 *     </SessionContext>
 *   );
 * }
 * ```
 */
export function useChatSession(threadId: string): Session {
  const queryClient = useQueryClient();

  const buildSession = (): Session =>
    createChatSession({
      queryClient,
      sessionId: threadId,
      reservedNames: CHAT_RESERVED_NAMES,
    });

  const [state, setState] = useState(() => ({ threadId, session: buildSession() }));

  // Thread switched mid-mount: create that thread's session now (during
  // render, before children read it) so a thread switch repoints the
  // namespace without an effect round-trip or a stale frame.
  if (state.threadId !== threadId) {
    const next = { threadId, session: buildSession() };
    setState(next);
    return next.session;
  }

  return state.session;
}
